<script>
  import { SPRITESHEET_URL } from '../../utils/constants.ts';
  import { formatPhone } from '../../utils/phoneFormatter.ts';
  import { getStaticMapImageSrc } from '../../utils/static-map.ts';
  import BaseLink from '../BaseLink/BaseLink.svelte';
  import BaseHeading from '../BaseHeading/BaseHeading.svelte';
  import { page } from '$app/stores';

  let {
    alt,
    getDirectionsText,
    hoursHeadingTemplate = $bindable(),
    className = '',
    lat = undefined,
    lng = undefined,
    name,
    addressLine1,
    addressLine2 = undefined,
    city,
    state,
    zip,
    phone,
    open,
    hours,
    services,
    pharmacySavingsCardButton,
    actionLinks,
    lazyloadImage = false,
    headingLevel = 'h3',
    dataTestId = 'pharmacy-location-information',
  } = $props();

  // Don't show hours heading if string replacements haven't been made
  hoursHeadingTemplate =
  hoursHeadingTemplate && !hoursHeadingTemplate.includes('{{PHARMACY}}') ? hoursHeadingTemplate : '';

  const userAgent = $page.data && $page.data.userAgent;
  const mapboxDetails = {
    lat,
    lng,
    userAgent
  }

  const mobileOptions = {
    width: 384,
    height: 100,
    ...mapboxDetails
  };

  const tabletOptions = {
    width: 672,
    height: 100,
    ...mapboxDetails
  };

  const desktopOptions = {
    width: 280,
    height: 240,
    ...mapboxDetails
  };

  const mobileDefaultSrc = getStaticMapImageSrc(mobileOptions);
  const mobileHighResSrc = getStaticMapImageSrc({ ...mobileOptions, highRes: true });
  const tabletDefaultSrc = hours ? getStaticMapImageSrc(tabletOptions) : getStaticMapImageSrc(desktopOptions);
  const tabletHighResSrc = hours
    ? getStaticMapImageSrc({ ...tabletOptions, highRes: true })
    : getStaticMapImageSrc({ ...desktopOptions, highRes: true });
  const desktopDefaultSrc = getStaticMapImageSrc(desktopOptions);
  const desktopHighResSrc = getStaticMapImageSrc({ ...desktopOptions, highRes: true });

  let url = `https://www.google.com/maps/dir//${addressLine1.replace(/ /g, '+')}`;

  if (city) {
    url = `${url},+${city.replace(/ /g, '+')}`;
  }

  if (state) {
    url = `${url},+${state.replace(/ /g, '+')}`;
  }

  if (zip) {
    url = `${url},+${zip}`;
  }

  const getDirectionsURL = `${url}/@${lat},${lng}`;
</script>

<svelte:head>
  <link rel="preload" as="image" href={mobileHighResSrc} />
</svelte:head>

<div
  class="rounded-lg shadow-y-sm bg-white lg:shadow-y-lg mx-auto overflow-hidden max-w-96 md:max-w-168 lg:max-w-none {className}"
  data-testid={dataTestId}
  itemscope={true}
  itemtype="http://schema.org/Pharmacy"
>
  <div class="flex flex-col lg:flex-row lg:items-stretch" class:md:flex-row={!hours}>
    <div
      class="h-25 lg:h-auto"
      class:lg:min-w-65={hours}
      class:xl:min-w-70={hours}
      class:md:h-auto={!hours}
      class:md:min-w-65={!hours}
    >
      <picture>
        <source media="(min-width: 1024px)" srcset={`${desktopDefaultSrc}, ${desktopHighResSrc} 2x`} />
        <source media="(min-width: 768px)" srcset={`${tabletDefaultSrc}, ${tabletHighResSrc} 2x`} />
        <source media="(max-width: 767px)" srcset={`${mobileDefaultSrc}, ${mobileHighResSrc} 2x`} />
        <img
          class="object-cover h-25 w-full md:min-h-full md:h-full"
          loading={lazyloadImage === true ? 'lazy' : 'eager'}
          itemprop="hasMap"
          src={mobileDefaultSrc}
          {alt}
        />
      </picture>
    </div>
    <div class="md:min-w-106 grow" class:md:flex={hours} class:md:justify-between={hours}>
      <div
        class="p-4 md:p-8 lg:p-10"
        class:md:flex={!hours}
        class:h-full={!hours}
        class:md:flex-col={!hours}
        class:md:justify-between={!hours}
      >
        <BaseHeading
          className="text-lg leading-tight text-blue-navy mb-2 md:text-2xl lg:text-3xl font-medium"
          itemprop="name"
          {headingLevel}>{name}</BaseHeading
        >
        <div
          class="text-base lg:text-lg text-gray-brownish mb-4 md:my-4 max-w-80 break-words uppercase"
          itemprop="address"
          itemscope={true}
          itemtype="http://schema.org/PostalAddress"
        >
          <div itemprop="streetAddress">{addressLine1}</div>
          {#if addressLine2}
            <div itemprop="streetAddress">{addressLine2}</div>
          {/if}
          <div>
            <span itemprop="addressLocality">{city},</span>
            <span itemprop="addressRegion">{state}</span>
            <span itemprop="postalCode">{zip}</span>
          </div>
        </div>
        <span itemprop="geo" itemscope itemtype="https://schema.org/GeoCoordinates">
          <meta itemprop="latitude" content={lat} />
          <meta itemprop="longitude" content={lng} />
        </span>
        <div class="flex md:mt-6">
          <div class="group inline-block mr-4 whitespace-nowrap">
            <svg
              class="h-4 w-4 md:h-6 md:w-6 inline-block mr-1 -mt-1 md:-mt-2 stroke-current text-blue-cerulean
              hover-hover:group-hover:text-blue-darkish group-active:text-blue-darkish"
              role="presentation"
            >
              <use xlink:href="{SPRITESHEET_URL}#phone" />
            </svg>
            <BaseLink
              className="text-sm lg:text-base leading-normal font-medium text-blue-cerulean hover-hover:group-hover:text-blue-darkish
              hover-hover:hover:underline active:underline group-active:text-blue-darkish"
              url="tel:{phone}"
              itemprop="telephone"
              content={`+1${phone}`}
              dataTestId="pharmacy-location-information-phone-link"
            >
              {formatPhone(phone)}
            </BaseLink>
          </div>
          <div class="group inline-block ml-4 whitespace-nowrap">
            <svg
              class="h-6 w-6 md:h-8 md:w-8 inline-block mr-1 -mt-1 md:-mt-2 stroke-current text-blue-cerulean
              hover-hover:group-hover:text-blue-darkish group-active:text-blue-darkish"
              role="presentation"
            >
              <use xlink:href="{SPRITESHEET_URL}#car" />
            </svg>
            <BaseLink
              className="text-sm lg:text-base leading-normal font-medium text-blue-cerulean hover-hover:group-hover:text-blue-darkish
              hover-hover:hover:underline active:underline group-active:text-blue-darkish"
              url={`${getDirectionsURL}`}
              dataTestId="pharmacy-location-information-directions-link"
              itemprop="hasMap"
            >
              {getDirectionsText}
            </BaseLink>
          </div>
        </div>
      </div>
      {#if (hours && hours.length) || open}
        <div class="border-t border-gray-light-silver md:border-l md:border-t-0"></div>
        <div class="p-4 md:p-8 md:grow md:min-w-56 lg:p-10 lg:min-w-80">
          {#if hoursHeadingTemplate}
            {#if headingLevel === 'h1'}
              <h2 class="font-medium mb-2 md:mb-3 md:text-lg md:mt-1 lg:mt-2">{hoursHeadingTemplate}</h2>
            {/if}
            {#if headingLevel === 'h3'}
              <h3 class="font-medium mb-2 md:mb-3 md:text-lg md:mt-1 lg:mt-2">{hoursHeadingTemplate}</h3>
            {/if}
          {/if}
          {#if open}
            <p>{open}</p>
          {/if}
          {#each hours as hour}
            <div class="flex text-sm mb-1 lg:text-base">
              {#if hour.openingHoursMeta}
                <meta itemprop="openingHours" content={`${hour.openingHoursMeta}`} />
              {/if}
              <span class="w-1/2">{hour.day}</span>
              <span class="ml-2">{hour.time}</span>
            </div>
          {/each}

          {#if (pharmacySavingsCardButton && !actionLinks) || (actionLinks && !actionLinks.length)}
            <BaseLink
              className="block w-full mt-6 px-4 py-2 lg:py-3 text-white text-center bg-blue-cerulean font-medium text-sm lg:text-0.5sm rounded 
              hover-hover:hover:bg-blue-darkish active:bg-blue-darkish focus:outline-none focus:shadow-focus active:shadow-active"
              {...pharmacySavingsCardButton}
            />
          {/if}
        </div>
      {/if}
    </div>
  </div>
  {#if services && services.length}
    <div class="flex w-full text-sm md:text-base bg-blue-darkish font-medium text-white justify-center p-3">
      {#each services as service, index}
        {#if index != 0}<span>&bull;</span>{/if}
        <span
          itemprop="amenityFeature"
          itemscope
          itemtype="https://schema.org/LocationFeatureSpecification"
          class="px-2"
        >
          <span itemprop="name">
            {service.label}
          </span>
          <meta itemprop="value" content="True" />
        </span>
      {/each}
    </div>
  {/if}
</div>

{#if actionLinks && actionLinks.length}
  <div class="mx-auto max-w-96 md:max-w-168 lg:max-w-none mt-4 grid gap-4 md:grid-cols-3">
    {#each actionLinks as action}
      <BaseLink
        className="flex items-center justify-center text-center w-full px-4 py-3 md:px-2 lg:min-h-12 text-white bg-blue-cerulean font-medium text-sm lg:text-0.5sm rounded 
          hover-hover:hover:bg-blue-darkish active:bg-blue-darkish focus:outline-none focus:shadow-focus active:shadow-active"
        {...action}
      />
    {/each}

    {#if pharmacySavingsCardButton}
      <BaseLink
        className="flex items-center justify-center text-center w-full px-4 py-3 md:px-2 lg:min-h-12 text-white bg-blue-cerulean font-medium text-sm lg:text-0.5sm rounded 
            hover-hover:hover:bg-blue-darkish active:bg-blue-darkish focus:outline-none focus:shadow-focus active:shadow-active"
        {...pharmacySavingsCardButton}
      />
    {/if}
  </div>
{/if}
